import React, {useState} from 'react';

import {Icon} from '@albert-io/atomic';
import type {IconProp, IconStyle} from '@albert-io/atomic/atoms/Icon/Icon.react';

interface Props {
  icon: IconProp;
  /** Required: `Icon` defaults to solid, and most glyphs here are registered in one style only. */
  iconStyle: IconStyle;
  title: React.ReactNode;
  /**
   * Rendered under the title. Receives whether the row is hovered or focused, so rows that reveal an
   * exact date on hover can swap their copy.
   */
  renderSubtitle: (isHovered: boolean) => React.ReactNode;
  /** Trailing icon buttons. Their clicks are kept from also opening the row. */
  actions: React.ReactNode;
  onOpen: () => void;
  'data-testid': string;
}

export const ClassworkRow = ({
  icon,
  iconStyle,
  title,
  renderSubtitle,
  actions,
  onOpen,
  'data-testid': testId
}: Props) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    // Clicking the row opens it; the trailing buttons stop propagation so they act on their own.
    // Mouse-only convenience on top of the keyboard-accessible action buttons.
    // oxlint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      className='gap-x2 rounded-x1 p-x1 hover:background-primary-hover active:background-primary-pressed flex cursor-pointer items-center'
      data-testid={testId}
      onClick={onOpen}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
    >
      <span className='h-x5 w-x5 background-special-subtle content-special flex shrink-0 items-center justify-center rounded-full'>
        <Icon color='inherit' icon={icon} iconStyle={iconStyle} />
      </span>
      <div className='min-w-x0 flex grow flex-col'>
        {title}
        {renderSubtitle(isHovered)}
      </div>
      {/* oxlint-disable-next-line jsx-a11y/click-events-have-key-events */}
      <div className='gap-x0-half flex shrink-0 items-center' onClick={(e) => e.stopPropagation()}>
        {actions}
      </div>
    </div>
  );
};
